import { cn } from "@/lib/utils";

export type BasemapId = "osm" | "esri";

export const BASEMAPS: Record<BasemapId, { url: string; attribution: string; maxZoom: number; labelsUrls?: string[] }> = {
  osm: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
  },
  esri: {
    // Esri World Imagery — free tile service, no API key required. Raw
    // satellite imagery carries no labels, so two transparent reference
    // overlays are stacked on top: roads/street names, then place/boundary
    // names — Esri's standard "hybrid" combo for World_Imagery.
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri",
    maxZoom: 19,
    labelsUrls: [
      "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}",
      "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
    ],
  },
};

/** Floating mini tab menu (top-right) to switch the active tile layer. */
export function BasemapSwitcher({
  value,
  onChange,
  className,
}: {
  value: BasemapId;
  onChange: (id: BasemapId) => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "absolute right-3 top-3 z-[400] flex items-center gap-0.5 rounded-full border bg-surface/90 p-0.5 text-xs font-medium shadow-sm backdrop-blur",
        className,
      )}
    >
      {(["osm", "esri"] as const).map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          className={cn(
            "rounded-full px-2.5 py-1 transition-colors",
            value === id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-surface-muted",
          )}
        >
          {id === "osm" ? "Map" : "Satellite"}
        </button>
      ))}
    </div>
  );
}
